import { useTranslation } from 'react-i18next';
import MaskIcon from '@/components/ui/MaskIcon';
import { formatTicketTime } from '@/features/waiter/ticketTime';

interface Props {
  kind: 'dine_in' | 'takeaway';
  label: string;
  covers: number;
  openedAt: string;
  onBack: () => void;
  /** SC-935 — état de la bascule « Emporter » temporaire de la table. */
  emporter?: boolean;
  /** SC-935 — absent (ou note Emporter) ⇒ pas de bascule dans l'en-tête. */
  onToggleEmporter?: () => void;
  /** SC-976 — design §5.3: `label` est déjà l'étiquette de tablée
   *  (« 5+6 », jamais recomposée ici) ; ce booléen ajoute seulement le
   *  pictogramme ⛓ décoratif à côté. */
  merged?: boolean;
  /** SC-1466 — note Emporter : toucher le nom l'ouvre pour le changer.
   *  Absent ⇒ le nom reste un simple texte. */
  onRename?: () => void;
  /** SC-1466 — le nom est encore le « Client N » d'un « Plus tard ». */
  labelProvisional?: boolean;
}

/**
 * 04-ux-mobile-spec.md §2 — `← · « N prs » · « N° 12 » or label · HH:MM`,
 * 48 px tall. Purely presentational: `TicketPage` owns the leave guard behind
 * `onBack` and the re-join / SSE-closed logic that decides what to render.
 *
 * SC-935 — une table porte en plus, à droite de l'heure, la bascule
 * « Emporter » : la tournée en cours se vend à emporter (carte, dispos et
 * prix emporter) sans quitter la table. `TicketPage` owns the state — and
 * owns the fact qu'il ne survit pas à la sortie de l'écran.
 */
export default function TicketHeader({
  kind,
  label,
  covers,
  openedAt,
  onBack,
  emporter = false,
  onToggleEmporter,
  merged = false,
  onRename,
  labelProvisional = false,
}: Props) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="ticket-header"
      className="grid grid-cols-[48px_1fr_auto] items-center bg-paper border-b-2 border-hairline h-12"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <button
        type="button"
        data-testid="ticket-back"
        aria-label={t('waiter.ticket.header.back')}
        onClick={onBack}
        className="w-12 h-12 flex items-center justify-center font-sans text-2xl leading-none text-ink bg-transparent border-0"
      >
        ←
      </button>
      <div className="min-w-0 flex items-center justify-center gap-2">
        {kind === 'dine_in' && (
          <span data-testid="ticket-covers" className="font-sans text-[18px] font-semibold text-ink-mute whitespace-nowrap">
            {t('waiter.ticket.header.covers', { n: covers })}
          </span>
        )}
        {kind === 'takeaway' && onRename ? (
          <button
            type="button"
            data-testid="ticket-label"
            onClick={onRename}
            className="min-w-0 h-11 flex items-center gap-1.5 bg-transparent border-0 p-0 pe-2 text-ink"
          >
            {/* Le texte visible (« + Nom ») reste le nom accessible ; ceci le complète. */}
            <span className="sr-only">{t('waiter.ticket.header.rename')} : </span>
            <span className="font-sans text-[20px] font-bold truncate">{label}</span>
            {labelProvisional ? (
              <span
                data-testid="ticket-add-name"
                className="shrink-0 h-7 px-2 rounded-full border-2 border-ink flex items-center font-sans text-[14px] font-bold whitespace-nowrap"
              >
                {t('waiter.ticket.header.addName')}
              </span>
            ) : (
              <span aria-hidden="true" className="shrink-0 font-sans text-base text-ink-mute">
                ✎
              </span>
            )}
          </button>
        ) : (
          <span data-testid="ticket-label" className="font-sans text-[20px] font-bold truncate">
            {kind === 'dine_in' ? t('waiter.ticket.header.tableLabel', { table: label }) : label}
            {kind === 'dine_in' && merged && (
              <span data-testid="ticket-merged-mark" aria-hidden="true">
                {' '}
                ⛓
              </span>
            )}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 pe-2">
        <span data-testid="ticket-time" className="font-mono text-[18px] font-bold tabular-nums whitespace-nowrap">
          {formatTicketTime(openedAt)}
        </span>
        {kind === 'dine_in' && onToggleEmporter && (
          // `role="switch"` plutôt qu'un bouton nu : l'état est ce qui compte
          // ici, et il doit s'entendre comme il se voit — plein violet
          // (`--w-online`, la couleur « Emporter » du mode serveur) quand la
          // tournée part à emporter, contour seul quand elle reste sur place.
          <button
            type="button"
            data-testid="ticket-emporter-toggle"
            role="switch"
            aria-checked={emporter}
            aria-label={t('waiter.ticket.header.emporterToggle')}
            onClick={onToggleEmporter}
            className={`h-11 px-2 rounded-btn-sm border-2 flex items-center gap-1 font-sans text-[15px] font-bold whitespace-nowrap ${
              emporter ? 'text-white' : 'text-ink bg-paper'
            }`}
            style={{
              borderColor: 'var(--w-online)',
              backgroundColor: emporter ? 'var(--w-online)' : undefined,
            }}
          >
            <MaskIcon src="/img/icon-takeaway-bag.svg" className="w-5 h-5" />
            {t('waiter.ticket.header.emporter')}
          </button>
        )}
      </div>
    </div>
  );
}
